import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../Food/RecipeList.css";
import "./BabyTracker.css";
import {
  defaultBabyEventForm,
  deleteBabyEvent,
  eventCategoryLabels,
  eventCategoryPluralLabels,
  eventTypeOptionsForCategory,
  bottleMilkTypes,
  bottleMilkTypeForEvent,
  feedingSides,
  feedingUnitForType,
  fetchBabyEvents,
  normalizeFeedingType,
  saveBabyEvent,
  sleepEventDisplay,
} from "./BabyTrackerShared";

const categoryIntro = {
  diaper: "Track wet, dirty, mixed, and dry diaper checks.",
  feeding: "Track breast feeding, bottle feeds, amounts, and notes.",
  sleep: "Track sleep and wake-up events for the daily timeline.",
};

const defaultTypeForCategory = (category) => eventTypeOptionsForCategory(category)[0];

const BabyTrackerCategoryPage = ({ category }) => {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(defaultBabyEventForm(category, defaultTypeForCategory(category)));
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const isFeeding = category === "feeding";
  const isBreastFeeding = isFeeding && form.eventType === "Breast";
  const isBottleFeeding = isFeeding && form.eventType === "Bottle";
  const title = eventCategoryPluralLabels[category];

  const refreshEvents = useCallback(() => {
    fetchBabyEvents(category).then((data) => setEvents(data || []));
  }, [category]);

  useEffect(() => {
    setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
    setEditingId(null);
    setShowForm(false);
    setStatusMessage("");
  }, [category]);

  useEffect(() => {
    refreshEvents();
  }, [refreshEvents]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((currentValue) => {
      if (name === "eventType" && category === "feeding") {
        return {
          ...currentValue,
          eventType: value,
          feedingUnit: feedingUnitForType(value),
          feedingMilkType: value === "Bottle" ? currentValue.feedingMilkType || "Formula" : "",
          feedingSide: value === "Breast" ? currentValue.feedingSide : "",
        };
      }
      return { ...currentValue, [name]: value };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setStatusMessage("");
    const eventForm = {
      ...form,
      endedAt: category === "sleep" ? "" : form.endedAt,
      feedingUnit: category === "feeding" ? feedingUnitForType(form.eventType) : "",
      feedingMilkType: category === "feeding" && form.eventType === "Bottle" ? form.feedingMilkType || "Formula" : "",
      feedingSide: category === "feeding" && form.eventType === "Breast" ? form.feedingSide : "",
    };
    saveBabyEvent(eventForm, editingId)
      .then(() => {
        setStatusMessage(editingId ? `${eventCategoryLabels[category]} event updated.` : `${eventCategoryLabels[category]} event logged.`);
        setEditingId(null);
        setShowForm(false);
        setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
        refreshEvents();
      })
      .catch(() => {
        setStatusMessage("Unable to save event.");
      });
  };

  const beginEdit = (entry) => {
    setEditingId(entry.id);
    setShowForm(true);
    setStatusMessage("");
    const normalizedEventType = normalizeFeedingType(entry.eventType);
    setForm({
      eventCategory: category,
      eventType: normalizedEventType,
      occurredAt: entry.occurredAtInput,
      endedAt: entry.endedAtInput || "",
      feedingAmount: entry.feedingAmount ?? "",
      feedingUnit: entry.feedingUnit || feedingUnitForType(normalizedEventType),
      feedingMilkType: normalizedEventType === "Bottle" ? bottleMilkTypeForEvent(entry) : "",
      feedingSide: normalizedEventType === "Breast" ? entry.feedingSide || "" : "",
      notes: entry.notes || "",
    });
  };

  const removeEvent = (eventId) => {
    deleteBabyEvent(eventId).then(() => {
      setStatusMessage(`${eventCategoryLabels[category]} event deleted.`);
      setEditingId(null);
      setShowForm(false);
      setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
      refreshEvents();
    });
  };

  return (
    <div className="BabyTrackerPage">
      <div className="searchBarDiv formViewWrapper BabyTrackerIntro">
        <div>
          <h2 className="BabyTrackerTitle">{title}</h2>
          <p className="BabyTrackerSubtitle">{categoryIntro[category]}</p>
        </div>
        <Link className="summaryButton BabyTrackerLinkButton" to="/baby-tracker">
          Home
        </Link>
      </div>

      <div className="BabyTrackerGrid">
        <div className="searchBarDiv formViewWrapper BabyTrackerPanel BabyTrackerSinglePanel">
          {!showForm ? (
            <button
              className="summaryButton BabyTrackerAddToggleButton"
              onClick={() => {
                setEditingId(null);
                setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
                setShowForm(true);
              }}
            >
              Add {eventCategoryLabels[category]}
            </button>
          ) : (
            <div className="BabyTrackerFormBlock">
              <h2 className="BabyTrackerTitle">{editingId ? `Edit ${eventCategoryLabels[category].toLowerCase()} event` : `Add ${eventCategoryLabels[category].toLowerCase()} event`}</h2>
              <form onSubmit={handleSubmit}>
                <div className="BabyTrackerTwoColumn">
                  <div className="inputDiv">
                    <label>Type</label>
                    <select
                      className="inputBox addInput"
                      name="eventType"
                      value={form.eventType}
                      onChange={handleChange}
                    >
                      {eventTypeOptionsForCategory(category).map((typeValue) => (
                        <option key={typeValue} value={typeValue}>
                          {typeValue}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="inputDiv">
                    <label>Occurred</label>
                    <input
                      type="datetime-local"
                      className="inputBox addInput"
                      name="occurredAt"
                      value={form.occurredAt}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {isFeeding ? (
                  <div className="BabyTrackerTwoColumn">
                    <div className="inputDiv">
                      <label>{isBreastFeeding ? "Time (minutes)" : "Amount (oz)"}</label>
                      <input
                        className="inputBox addInput"
                        name="feedingAmount"
                        value={form.feedingAmount}
                        onChange={handleChange}
                        inputMode="decimal"
                      />
                    </div>
                    {isBreastFeeding ? (
                      <div className="inputDiv">
                        <label>Started side</label>
                        <select
                          className="inputBox addInput"
                          name="feedingSide"
                          value={form.feedingSide}
                          onChange={handleChange}
                        >
                          {feedingSides.map((sideValue) => (
                            <option key={sideValue || "none"} value={sideValue}>
                              {sideValue || "N/A"}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    {isBottleFeeding ? (
                      <div className="inputDiv">
                        <label>Bottle type</label>
                        <select
                          className="inputBox addInput"
                          name="feedingMilkType"
                          value={form.feedingMilkType || "Formula"}
                          onChange={handleChange}
                        >
                          {bottleMilkTypes.map((milkType) => (
                            <option key={milkType} value={milkType}>
                              {milkType}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                <div className="inputDiv">
                  <label>Notes</label>
                  <textarea
                    className="inputBox textAreaAdd BabyTrackerTextarea"
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                  />
                </div>

                <div className="BabyTrackerActionRow">
                  <button type="submit" className="summaryButton">
                    Submit
                  </button>
                  <button
                    type="button"
                    className="summaryButton"
                    onClick={() => {
                      setEditingId(null);
                      setShowForm(false);
                      setForm(defaultBabyEventForm(category, defaultTypeForCategory(category)));
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
          {statusMessage ? <p className="BabyTrackerStatus">{statusMessage}</p> : null}
        </div>

        <div className="shoppingListDiv BabyTrackerPanel">
          <h2 className="BabyTrackerTitle">Recent {title.toLowerCase()}</h2>
          {events.length ? (
            events.map((entry) => {
              const sleepDisplay = category === "sleep" ? sleepEventDisplay(entry.eventType) : null;
              return (
                <div className={sleepDisplay ? "BabyTrackerHistoryCard BabyTrackerSleepCard" : "BabyTrackerHistoryCard"} key={entry.id}>
                  {sleepDisplay ? (
                    <div className="BabyTrackerSleepHeader">
                      <span className={`BabyTrackerSleepBadge BabyTrackerSleepBadge-${sleepDisplay.tone}`}>
                        {entry.eventType}
                      </span>
                      <div>
                        <strong>{sleepDisplay.label}</strong>
                        <div>{entry.occurredAt}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="BabyTrackerHistoryRow">
                      <div>
                        <strong>{category === "feeding" ? normalizeFeedingType(entry.eventType) : entry.eventType}</strong>
                        <div>{entry.occurredAt}</div>
                      </div>
                      {entry.endedAt ? <span>Ended {entry.endedAt}</span> : null}
                    </div>
                  )}
                  {entry.feedingAmount ? (
                    <div className="BabyTrackerHistoryMeta">
                      <span>
                        {entry.feedingAmount} {entry.feedingUnit}
                      </span>
                      {normalizeFeedingType(entry.eventType) === "Bottle" ? <span>{bottleMilkTypeForEvent(entry)}</span> : null}
                      {entry.feedingSide ? <span>{entry.feedingSide}</span> : null}
                    </div>
                  ) : null}
                  {entry.notes ? <p className="BabyTrackerNotes">{entry.notes}</p> : null}
                  <div className="BabyTrackerActionRow">
                    <button className="summaryButton" onClick={() => beginEdit(entry)}>
                      Edit
                    </button>
                    <button className="summaryButton" onClick={() => removeEvent(entry.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <p>No {title.toLowerCase()} logged yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BabyTrackerCategoryPage;
